import {
  Hr,
  CardHeading,
  CardSubHeading,
  CardText,
  CardRow,
} from "../../styles/CardStyles";
import { Button, Stack } from "@mui/material";
import { ChangeEvent, FC, useState } from "react";
import { IUserPoolData, OwnerCard } from "./PoolPage";
import { STAKING_POOL_CONFIG } from "../../config";
import BigNumber from "bignumber.js";
import { Contract } from "@ethersproject/contracts";
import { ethers } from "ethers/lib.esm";
import poolAbi from "../../config/abi/pool.json";
import erc20Abi from "../../config/abi/erc20.json";
import { Pool, Erc20 } from "../../config/abi/types";
import formatWeiToEth, { BIG_10 } from "../../utils/formatWeiToEth";
import ConnectWalletBtn from "../../components/ConnectWalletBtn";
import StyledModal from "../../components/StyledModal";
import { LoadingButton } from "@mui/lab";
import TxnStateModal from "../../components/TxnStateModal";
import CopyToClipboard from "../../components/CopyToClipboard";
import {
  getContractErrorActionHint,
  getContractErrorMessage,
} from "../../utils/getContractErrorMessage";

interface IStakingCard {
  account: string | null | undefined;
  userPoolData: IUserPoolData;
  pendingTxn: boolean;
  handleConnectWalletModalOpen: () => void;
  handleApprove: () => void;
  proof: string[] | [];
  userIsWhitelisted: boolean;
}

const StakingCard: FC<IStakingCard> = ({
  account,
  userPoolData,
  pendingTxn,
  handleConnectWalletModalOpen,
  handleApprove,
  proof,
  userIsWhitelisted,
}) => {
  const [amount, setAmount] = useState("0.0");
  const [stakeModalOpen, setStakeModalOpen] = useState(false);
  const [unstakeModalOpen, setUnstakeModalOpen] = useState(false);
  const [openPurchaseModal, setOpenPurchaseModal] = useState(false);
  const [txnPending, setTxnPending] = useState({
    loading: false,
    success: false,
    failure: false,
    errorMessage: undefined as string | undefined,
    errorHint: undefined as string | undefined,
  });

  const handleOpenPurchaseModal = () => setOpenPurchaseModal(true);
  const handleClosePurchaseModal = () => setOpenPurchaseModal(false);

  const handleStakeModalOpen = () => setStakeModalOpen(true);
  const handleUnstakeModalOpen = () => setUnstakeModalOpen(true);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAmount(event.target.value);
  };

  const handleStakeModalClose = () => setStakeModalOpen(false);
  const handleUnstakeModalClose = () => setUnstakeModalOpen(false);

  const handleStake = async () => {
    handleStakeModalClose();
    handleOpenPurchaseModal();
    setTxnPending({
      loading: true,
      success: false,
      failure: false,
      errorMessage: undefined,
      errorHint: undefined,
    });
    try {
      const stakeAmount = new BigNumber(amount)
        .multipliedBy(BIG_10.pow(STAKING_POOL_CONFIG.inputToken.decimals))
        .toFixed();
      const provider = new ethers.providers.Web3Provider(
        // @ts-ignore
        (window as WindowChain).ethereum
      );
      const signer = provider.getSigner();

      const poolContract = new Contract(
        STAKING_POOL_CONFIG.id,
        poolAbi,
        signer
      ) as Pool;

      const erc20Contract = new Contract(
        STAKING_POOL_CONFIG.inputToken.address,
        erc20Abi,
        signer
      ) as Erc20;

      if (!account) {
        setTxnPending({
          loading: false,
          success: false,
          failure: true,
          errorMessage: "No wallet connected",
          errorHint: "Connect your wallet and try again.",
        });
        return;
      }

      const allowanceOfInputToken = await erc20Contract.allowance(
        account,
        STAKING_POOL_CONFIG.id
      );

      const gasPrice = await provider.getGasPrice();
      if (
        new BigNumber(allowanceOfInputToken.toString()).isLessThan(stakeAmount)
      ) {
        const approveGasLimit = await erc20Contract.estimateGas.approve(
          STAKING_POOL_CONFIG.id,
          stakeAmount
        );
        const txn = await erc20Contract.approve(
          STAKING_POOL_CONFIG.id,
          stakeAmount,
          {
            gasLimit: approveGasLimit,
            gasPrice,
          }
        );
        await txn.wait();
      }
      const gasLimit = await poolContract.estimateGas.deposit(stakeAmount, proof);
      const depositTx = await poolContract.deposit(stakeAmount, proof, {
        gasLimit,
        gasPrice,
      });
      await depositTx.wait();
      setTxnPending({
        loading: false,
        success: true,
        failure: false,
        errorMessage: undefined,
        errorHint: undefined,
      });
    } catch (err) {
      const errorMessage = getContractErrorMessage(err);
      setTxnPending({
        loading: false,
        success: false,
        failure: true,
        errorMessage,
        errorHint: getContractErrorActionHint(errorMessage),
      });
      console.error("Error in handleStake: ", err);
    }
  };
  const handleUnstake = async () => {
    handleUnstakeModalClose();
    handleOpenPurchaseModal();
    setTxnPending({
      loading: true,
      success: false,
      failure: false,
      errorMessage: undefined,
      errorHint: undefined,
    });
    try {
      const stakeAmount = new BigNumber(amount)
        .multipliedBy(BIG_10.pow(STAKING_POOL_CONFIG.inputToken.decimals))
        .toFixed();

      const provider = new ethers.providers.Web3Provider(
        // @ts-ignore
        (window as WindowChain).ethereum
      );
      const signer = provider.getSigner();
      const poolContract = new Contract(
        STAKING_POOL_CONFIG.id,
        poolAbi,
        signer
      ) as Pool;
      const gasPrice = await provider.getGasPrice();
      const gasLimit = await poolContract.estimateGas.withdraw(stakeAmount);
      const withdrawTx = await poolContract.withdraw(stakeAmount, { gasLimit, gasPrice });
      await withdrawTx.wait();
      setTxnPending({
        loading: false,
        success: true,
        failure: false,
        errorMessage: undefined,
        errorHint: undefined,
      });
    } catch (err) {
      const errorMessage = getContractErrorMessage(err);
      setTxnPending({
        loading: false,
        success: false,
        failure: true,
        errorMessage,
        errorHint: getContractErrorActionHint(errorMessage),
      });
      console.error("Error in handleUnstake: ", err);
    }
  };

  return (
    <>
      <TxnStateModal
        open={openPurchaseModal}
        handleClose={handleClosePurchaseModal}
        txnPending={txnPending}
      />
      {userPoolData && (
        <StyledModal
          heading={"Stake"}
          open={stakeModalOpen}
          handleClose={handleStakeModalClose}
          amount={amount}
          setAmount={setAmount}
          handleInputChange={handleInputChange}
          max={formatWeiToEth(
            userPoolData.inputTokenBalance,
            Number(STAKING_POOL_CONFIG.inputToken.decimals),
            6
          )}
          handleBtnClick={handleStake}
          pendingTxn={pendingTxn}
        />
      )}
      {userPoolData && (
        <StyledModal
          heading={"Unstake"}
          open={unstakeModalOpen}
          handleClose={handleUnstakeModalClose}
          amount={amount}
          setAmount={setAmount}
          handleInputChange={handleInputChange}
          max={formatWeiToEth(
            userPoolData.amount,
            Number(STAKING_POOL_CONFIG.inputToken.decimals),
            6
          )}
          handleBtnClick={handleUnstake}
          pendingTxn={pendingTxn}
        />
      )}
      <OwnerCard style={{ backgroundColor: "rgb(13, 13, 13)" }}>
        <CardHeading>Staking Info</CardHeading>
        <Hr />
        <Stack>
          <Stack
            direction={"row"}
            display={"flex"}
            justifyContent={"space-around"}
            alignItems={"center"}
            marginBottom={"10px"}
            columnGap={1}
          >
            <Stack direction={"row"}>
              <img
                src={`${STAKING_POOL_CONFIG.inputToken.tokenURL}`}
                alt={`${STAKING_POOL_CONFIG.inputToken.name} token`}
                width={"30px"}
                height={"30px"}
              />

              <Stack rowGap={0.5} marginLeft={"5px"}>
                <CardText>{STAKING_POOL_CONFIG.inputToken.name}</CardText>
                <CardSubHeading>
                  {STAKING_POOL_CONFIG.inputToken.symbol}
                </CardSubHeading>
              </Stack>
            </Stack>

            <Stack
              display={"flex"}
              alignItems={"center"}
              fontSize={"18px"}
              fontWeight={600}
              direction={"row"}
            >
              <CardSubHeading>
                {`${STAKING_POOL_CONFIG.inputToken.address.slice(
                  0,
                  4
                )}...${STAKING_POOL_CONFIG.inputToken.address.slice(
                  STAKING_POOL_CONFIG.inputToken.address.length - 4
                )}`}
              </CardSubHeading>
              <CopyToClipboard
                toCopy={STAKING_POOL_CONFIG.inputToken.address}
              />
            </Stack>
          </Stack>
          {account ? (
            <>
              <CardRow>
                <CardSubHeading>
                  {STAKING_POOL_CONFIG.inputToken.symbol} balance
                </CardSubHeading>
                <CardText>
                  {formatWeiToEth(
                    userPoolData?.inputTokenBalance ?? "1",
                    Number(STAKING_POOL_CONFIG.inputToken.decimals)
                  )}
                </CardText>
              </CardRow>
              <CardRow style={{ marginBottom: "20px" }}>
                <CardSubHeading>
                  {STAKING_POOL_CONFIG.inputToken.symbol} staked
                </CardSubHeading>
                <CardText>
                  {formatWeiToEth(
                    userPoolData?.amount ?? "1",
                    Number(STAKING_POOL_CONFIG.inputToken.decimals),
                    3
                  )}
                </CardText>
              </CardRow>
              {new BigNumber(userPoolData?.allowance ?? "0").isGreaterThan(
                0
              ) ? (
                <Stack direction={"row"} gap={1}>
                  <Button
                    variant={"contained"}
                    fullWidth
                    disabled={pendingTxn || !userIsWhitelisted}
                    onClick={handleStakeModalOpen}
                    style={{ backgroundColor: "rgb(236, 204, 93)", borderRadius: "10px", color: "rgba(0, 0, 0, 0.87)" }}
                  >
                    Stake
                  </Button>
                  <Button
                    variant={"outlined"}
                    fullWidth
                    disabled={pendingTxn}
                    onClick={handleUnstakeModalOpen}
                    style={{ backgroundColor: "rgba(235, 204, 93, 0.08)", borderRadius: "10px", color: "rgb(235, 204, 93)", border: "1px solid rgba(235, 204, 93, 0.5)" }}
                  >
                    Unstake
                  </Button>
                </Stack>
              ) : (
                <LoadingButton
                  variant={"outlined"}
                  onClick={handleApprove}
                  fullWidth
                  loading={pendingTxn}
                  style={{ backgroundColor: "rgba(235, 204, 93, 0.08)", borderRadius: "10px", color: "rgb(235, 204, 93)", border: "1px solid rgba(235, 204, 93, 0.5)" }}
                >
                  Connect asset
                </LoadingButton>
              )}
            </>
          ) : (
            <ConnectWalletBtn
              handleConnectWalletModalOpen={handleConnectWalletModalOpen}
            />
          )}
        </Stack>
      </OwnerCard >
    </>
  );
};

export default StakingCard;
